<script lang="ts" setup>
import { useRoute } from '#vue-router'
import { ROUTE_PATHS } from '@/utils/route'
import Loader from '@/components/Loader/Loader.vue'
import { useVideocastPage } from '@/domains/articles/composables/useVideocastPage'
import useVideocastPageHead  from '@/domains/articles/composables/useVideocastPageHead'
import { useRouteLink } from '@/composables/useRouteLink'

const { routeLink } = useRouteLink()
const location = useRoute()
const showVideocast = ref(true)

const { videocast, videocasts } = await useVideocastPage()

if (videocast.value) {
  showVideocast.value = true

  const { title, link, meta, I18nParams }  = useVideocastPageHead(videocast.value)
  const setI18nParams = useSetI18nParams()
  setI18nParams(I18nParams)
  useHead({
    title,
    meta,
    link,
  })
}
else {
    throw createError({
    statusCode: 400,
  })
}

watch(location, () => {
  showVideocast.value = false
})

</script>

<template>
  <div>
    <div
      v-if="!showVideocast || !videocast"
      class="videocast__loader"
    >
      <Loader />
    </div>
    <article
      v-if="showVideocast && videocast"
      class="videocast"
    >
      <header class="videocast__header">
        <h1 class="videocast__header_title">
          {{ videocast?.title }}
        </h1>
        <p class="videocast__header_subtitle">
          {{ videocast?.description }}
        </p>
        <div class="videocast__header_meta">
          <time
            v-if="videocast?.date"
            :datetime="videocast?.date"
            class="videocast__header_date"
          >
            {{ $t('articles.publication') }} {{ videocast?.date }}
          </time>
          <span
            v-if="videocast?.author.name"
            class="videocast__header_author"
          >
            {{ videocast?.author.name }}
          </span>
        </div>
        <div class="videocast__header_cover">
          <ClientOnly
            v-if="videocast?.youtubeId"
          >
            <iframe
              width="100%"
              :src="`https://www.youtube.com/embed/${videocast.youtubeId}`"
              :title="videocast?.title"
              frameborder="0"
              referrerpolicy="origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            />
          </ClientOnly>

          <iframe
            v-else-if="videocast?.vimeoId"
            :src="`https://player.vimeo.com/video/${videocast.vimeoId}`"
            width="100%"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
          />
        </div>

        <div
          v-if="videocast?.urls?.length"
          class="videocast__header_podcasts"
        >
          <p class="videocast__header_podcasts-description">
            {{ $t('videocasts.listenOn') }}
          </p>
          <p class="videocast__header_podcasts-description videocast__header_podcasts-description--desktop">
            {{ $t('videocasts.listenOnDesktop') }}
          </p>
          <div class="videocast__header_podcasts-elements">
            <a
              v-for="podcast in videocast?.urls"
              :key="podcast.type"
              class="videocast__header_podcasts-element"
              :href="podcast.url"
              target="_blank"
            ><img
              :src="podcast.icon"
              :alt="podcast.label"
            ><span>{{ podcast.label }}</span></a>
          </div>
        </div>
      </header>
      <div
        class="videocast__body"
      >
        <div
          class="videocast__content"
          v-html="videocast?.content"
        />
      </div>
      <div
        v-if="videocast?.author.image"
        class="videocast__author"
      >
        <img
          :src="videocast?.author.image.url"
          :alt="videocast?.author.name"
          class="videocast__author_image"
        >
        <div class="videocast__author_content">
          <h4 class="videocast__author_name">
            {{ videocast?.author.name }}
          </h4>
          <p class="videocast__author_title">
            {{ videocast?.author.title }}
          </p>
          <p class="videocast__author_description">
            {{ videocast?.author.description }}
          </p>
        </div>
      </div>
    </article>
    <div class="videocast__articles">
      <CardCarousel
        v-if="videocasts"
        :header="$t('videocasts.header')"
        color="gray"
        :slides="videocasts"
        :button="{
          text: $t('videocasts.buttonText'),
          to: routeLink(ROUTE_PATHS.ARTICLES),
        }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.videocast {
  margin: 0 auto;
  padding: 8.8rem 2.4rem 4.8rem 2.4rem;
  background-color: $gray-100;

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: calc(100vh - 8.8rem);
  }

  &__header {

    @include atM {
      max-width: 120rem;
      margin: 6.4rem auto 0 auto;
    }

    &_title {
      font-weight: 500;
      font-size: 3.2rem;
      line-height: 4rem;
      text-align: center;

      @include atM {
        max-width: 111.6rem;
        margin: 0 auto;
        font-size: 6rem;
        line-height: 8.8rem;
      }
    }

    &_subtitle {
      margin-top: 1.6rem;
      font-weight: 500;
      font-size: 2rem;
      line-height: 2.8rem;
      text-align: center;

      @include atM {
        max-width: 83rem;
        margin: 3.2rem auto 0 auto;
        font-size: 2.4rem;
        line-height: 3.2rem;
      }
    }

    &_meta {
      display: block;
      margin-top: 3.2rem;
      text-align: center;

      @include atM {
        margin-top: 4.8rem;
      }
    }

    &_date,
    &_author {
      display: inline-block;
      color: $text-200;
      font-weight: 500;
      font-size: 1.6rem;
      line-height: 2.4rem;
    }

    &_date {
      padding-right: 1.6rem;
      color: $text-200;
    }

    &_author {
      padding-left: 1.6rem;
      color: $text-300;
      border-left: 0.1rem solid $gray-300;
    }

    &_cover {
      width: 100%;
      margin: 2.8rem auto 1.2rem;
      overflow: hidden;
      border-radius: 1.2rem;

      iframe {
        aspect-ratio: 16/9;
      }

      @include atM {
        max-width: 100rem;
        margin: 5.6rem auto 0.8rem;
      }
    }

    &_image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    &_podcasts {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1.2rem;
      width: 100%;
      margin: 0 auto 2.4rem;
      padding: 1.2rem 3rem;
      background-color: $gray-100;
      border: 1px solid $gray-300;
      border-radius: 1.6rem;

      @include atM {
        flex-direction: row;
        gap: 4rem;
        max-width: 100rem;
        margin: 0 auto 5.6rem;
        padding: 2rem 3rem;
      }

      &-description {
        display: block;
        color: $text-400;
        font-weight: 500;
        font-size: 1.8rem;
        line-height: 2.4rem;

        @include atM {
          display: none;
        }

        &--desktop {
          display: none;

          @include atM {
            display: block;
          }
        }
      }
      &-elements {
        display: flex;
        align-items: center;
        gap: 2.4rem;

        @include atM {
          gap: 0.8rem;
        }
      }

      &-element {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        width: 5.6rem;
        padding: 1.2rem 1.6rem;
        background-color: $white;
        border-radius: 0.8rem;

        @include atM {
          width: 18.8rem;
        }

        & span {
          display: none;
          color: $text-400;
          font-weight: 500;
          font-size: 1.6rem;
          line-height: 2.4rem;

          @include atM {
            display: block;
          }
        }
      }
    }
  }

  &__body {
    padding-bottom: 4rem;
    border-bottom: 0.1rem solid $gray-300;

    @include atM {
      padding-bottom: 5.6rem;
    }

    &_icons {
      display: flex;
      gap: 0.8rem;
      margin-bottom: 1.6rem;
    }
  }

  &__content {

    :deep() {
      hr {
        height: 0.1rem;
        margin: 0  0 2.4rem 0;
        background: $gray-300;
      }

      .richtext__imgContainer, .richtext__imgContainer a {
        display: flex;
        align-items: center;
        width: 100%;
      }

      .richtext__img {
        max-width: 900px;
        width: 100%;
        margin: 40px auto;
        aspect-ratio: 16 / 9;
      }

      h1,
      h2,
      h3,
      h4,
      h5 {
        margin-bottom: 2.4rem;
        font-weight: 500;
        line-height: 1.2;
      }

      h1 {
        font-size: 2.8rem;
      }

      h2 {
        font-size: 2.6rem;
      }

      h3 {
        font-size: 2.4rem;
      }

      h4 {
        font-size: 2.2rem;
      }

      h5 {
        font-size: 2rem;
      }

      p {
        margin-bottom: 2.4rem;
        font-weight: 400;
        font-size: 1.8rem;
        line-height: 2.4rem;
      }

      b, strong {
        font-weight: 600;
      }

      i {
        font-style: italic;
      }

      u {
        text-decoration: underline;
      }

      a {
        color: $link;
        font-weight: 400;
      }

      ul, ol {
        padding-left: 2rem;
        li {
          &::marker {
            font-size: 1.8rem;
          }

          p {
            margin-bottom: 0.8rem
          }
        }
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      blockquote {
        padding-left: 1.6rem;
        border-left: 0.8rem solid $gray-300;
      }

      table {
        width: 100%;
        margin-bottom: 2.4rem;
        font-weight: 400;
        border: 1px solid $gray-300;

        tr {
          &:nth-of-type(odd) {
            background-color: $white;
          }

          &:nth-of-type(even) {
            background-color: $gray-100;
          }

          th, td {
            padding: 1.6rem;
            color: $text-400;
            font-weight: 400;
            font-size: 1.4rem;
            border: 1px solid $gray-300;

            p {
              margin-bottom: 0;
            }

            &:first-of-type {
              font-weight: 600;
            }
          }

          th {
            font-weight: 600;
          }
        }
      }
    }

    @include atM {
      max-width: 100rem;
      margin: 0 auto;

      :deep() {
        h1,
        h2,
        h3,
        h4,
        h5 {
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        h1 {
          font-size: 4rem;
        }

        h2 {
          font-size: 3.6rem;
        }

        h3 {
          font-size: 3.2rem;
        }

        h4 {
          font-size: 2.8rem;
        }

        h5 {
          font-size: 2.4rem;
        }

        p {
          margin-bottom: 4.8rem;
          font-size: 2rem;
          line-height: 3rem;
        }

        ul, ol {
          li {
            &::marker {
              font-size: 2rem;
            }
          }
        }

        blockquote {
          padding-left: 2.4rem;
        }

        table {
          margin-bottom: 4.8rem;
          th, td {
            font-size: 1.6rem;
          }
        }
      }
    }
  }

  &__author {
    display: flex;
    gap: 2.4rem;
    padding: 4rem 0;
    border-bottom: 0.1rem solid $gray-300;

    @include atM {
      gap: 4rem;
      max-width: 100rem;
      margin: 0 auto;
      padding: 5.6rem 0;
    }

    &_image {
      min-width: 7.2rem;
      width: 7.2rem;
      height: 7.2rem;
      overflow: hidden;
      border-radius: 50%;
      object-fit: cover;

      @include atM {
        min-width: 15rem;
        width: 15rem;
        height: 15rem;
      }
    }

    &_name {
      margin-bottom: 1.6rem;
      font-weight: 500;
      font-size: 2.4rem;
      line-height: 3.2rem;
    }

    &_title {
      margin-bottom: 1.6rem;
      color: $text-200;
      font-weight: 500;
      font-size: 2rem;
      line-height: 2.4rem;
    }

    &_description {
      font-weight: 400;
      font-size: 2rem;
      line-height: 3rem;
    }
  }
}
</style>
